import React, { useEffect, useRef } from 'react';

interface AudioWaveVisualizerProps {
  stream: MediaStream | null;
  isActive: boolean;
  isPaused: boolean;
}

export const AudioWaveVisualizer: React.FC<AudioWaveVisualizerProps> = ({ 
  stream, 
  isActive,
  isPaused 
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!stream || !canvasRef.current || !isActive) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Setup Web Audio API
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (isPaused) {
        // Vẽ bars tĩnh khi pause
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const barWidth = canvas.width / 20;
        const centerY = canvas.height / 2;
        
        for (let i = 0; i < 20; i++) {
          const x = i * barWidth;
          const barHeight = 3;
          ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
          ctx.fillRect(x, centerY - barHeight / 2, barWidth - 2, barHeight);
        }
        
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barCount = 20;
      const barWidth = canvas.width / barCount;
      const centerY = canvas.height / 2;

      for (let i = 0; i < barCount; i++) {
        const dataIndex = Math.floor((i * bufferLength) / barCount);
        const value = dataArray[dataIndex];
        const barHeight = (value / 255) * canvas.height * 0.8;
        
        const x = i * barWidth;
        const y = centerY - barHeight / 2;

        // Gradient color based on height
        const alpha = 0.6 + (value / 255) * 0.4;
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fillRect(x, y, barWidth - 2, barHeight);
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stream, isActive, isPaused]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={80}
      style={{
        width: '100%',
        maxWidth: '300px',
        height: '80px',
      }}
    />
  );
};

